import store from '@/state/store';
import { authService } from '@/api/auth';

export class ViewerAuth {
  constructor() {
    this.token = null;
    this.userRole = null;
    this.permissions = {};
  }

  async validateAccess(studyId, requiredPermissions = []) {
    try {
      // Verificar se o usuário está autenticado
      if (!store.getters['auth/loggedIn']) {
        throw new Error('User not authenticated');
      }

      // Obter token atual
      this.token = store.getters['auth/accessToken'] || localStorage.getItem('token');
      if (!this.token) {
        throw new Error('No access token found');
      }

      // Verificar validade do token
      const isValid = await this.validateToken();
      if (!isValid) {
        throw new Error('Invalid or expired token');
      }

      // Obter role do usuário
      this.userRole = this.getUserRole();
      if (!this.userRole) {
        throw new Error('User role not found');
      }

      // Verificar permissões específicas
      const hasPermission = await this.checkStudyPermissions(studyId, requiredPermissions);
      if (!hasPermission) {
        throw new Error('Insufficient permissions to view this study');
      }

      return {
        success: true,
        token: this.token,
        userRole: this.userRole,
        permissions: this.permissions,
      };
    } catch (error) {
      console.error('Access validation failed:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  async validateToken() {
    try {
      // Verificar se o token não expirou
      const tokenData = this.parseJWT(this.token);
      if (!tokenData) return false;

      const now = Date.now() / 1000;
      if (tokenData.exp && tokenData.exp < now) {
        return false;
      }

      // Validar com o servidor se necessário
      const response = await authService.validateToken();
      return response.data.valid;
    } catch (error) {
      console.error('Token validation error:', error);
      return false;
    }
  }

  getUserRole() {
    try {
      // Tentar obter do store primeiro
      const storeRole = store.getters['auth/userRole'];
      if (storeRole) return storeRole.toLowerCase();

      // Tentar obter do localStorage
      const userInfo = localStorage.getItem('userInfo');
      const loggedUser = localStorage.getItem('user');

      if (userInfo) {
        const userInfoData = JSON.parse(userInfo);
        return userInfoData.role ? userInfoData.role.toLowerCase() : null;
      }

      if (loggedUser) {
        const userData = JSON.parse(loggedUser);
        return userData.role ? userData.role.toLowerCase() : null;
      }

      return null;
    } catch (error) {
      console.error('Error getting user role:', error);
      return null;
    }
  }

  async checkStudyPermissions(studyId, requiredPermissions = []) {
    try {
      // Verificar permissões básicas por role
      const rolePermissions = this.getRolePermissions(this.userRole);
      
      // Verificar se o role tem permissão básica para visualizar
      if (!rolePermissions.canView) {
        return false;
      }

      // Se não há permissões específicas requeridas, permitir
      if (requiredPermissions.length === 0) {
        this.permissions = rolePermissions;
        return true;
      }

      // Verificar permissões específicas
      const hasAllPermissions = requiredPermissions.every(permission =>
        rolePermissions[permission] === true
      );

      if (hasAllPermissions) {
        this.permissions = rolePermissions;
        return true;
      }

      // Verificar permissões específicas do estudo no servidor se necessário
      // const studyPermissions = await this.getStudySpecificPermissions(studyId);
      // return this.hasRequiredPermissions(studyPermissions, requiredPermissions);

      return false;
    } catch (error) {
      console.error('Error checking study permissions:', error);
      return false;
    }
  }

  getRolePermissions(role) {
    const permissions = {
      admin: {
        canView: true,
        canMeasure: true,
        canAnnotate: true,
        canExport: true,
        canDelete: true,
        canEditMetadata: true,
        canAccessAdvancedTools: true,
        canViewAllStudies: true,
      },
      doctor: {
        canView: true,
        canMeasure: true,
        canAnnotate: true,
        canExport: true,
        canDelete: false,
        canEditMetadata: false,
        canAccessAdvancedTools: true,
        canViewAllStudies: false,
      },
      technician: {
        canView: true,
        canMeasure: true,
        canAnnotate: false,
        canExport: false,
        canDelete: false,
        canEditMetadata: false,
        canAccessAdvancedTools: false,
        canViewAllStudies: false,
      },
      patient: {
        canView: false,
        canMeasure: false,
        canAnnotate: false,
        canExport: false,
        canDelete: false,
        canEditMetadata: false,
        canAccessAdvancedTools: false,
        canViewAllStudies: false,
      },
    };

    return permissions[role] || permissions.patient;
  }

  parseJWT(token) {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error parsing JWT:', error);
      return null;
    }
  }

  getAuthHeaders() {
    return {
      'Authorization': `Bearer ${this.token}`,
      'Content-Type': 'application/json',
    };
  }

  // Método para renovar token se necessário
  async refreshTokenIfNeeded() {
    try {
      const tokenData = this.parseJWT(this.token);
      if (!tokenData) return false;

      const now = Date.now() / 1000;
      const timeUntilExpiry = tokenData.exp - now;

      // Se o token expira em menos de 5 minutos, tentar renovar
      if (timeUntilExpiry < 300) {
        const response = await authService.refreshToken();
        if (response.data.access_token) {
          this.token = response.data.access_token;
          store.dispatch('auth/setAccessToken', this.token);
          localStorage.setItem('token', this.token);
          return true;
        }
      }
      return true;
    } catch (error) {
      console.error('Error refreshing token:', error);
      return false;
    }
  }
}

export default ViewerAuth;
